import { getLogoStripClients } from '../utils/dataModel';

/**
 * Client logos under the hero copy.
 *
 * The logos range from square marks to wordmarks nearly 4x wider than they are tall,
 * so they are normalised to a common height and muted to a single visual weight —
 * otherwise Hart Energy and Good Cents dominate and Mastercard disappears.
 *
 * The track is rendered twice so the marquee can loop seamlessly; the second copy is
 * hidden from assistive tech. Under prefers-reduced-motion the animation is dropped
 * and the strip becomes a static wrapped row (see App.css).
 *
 * Not lazy-loaded: the rail moves by transform, which does not reliably trigger the
 * intersection check, so off-screen logos stayed unloaded and rendered 0px wide.
 * They are a few KB each and sit above the fold anyway.
 */
/**
 * Normalising purely by height leaves square marks tiny beside wide wordmarks —
 * Mastercard rendered 31px wide against Amplify's 98px. Tag the square ones from
 * their intrinsic ratio on load so CSS can give them more height and even out the
 * optical weight. Doing it from naturalWidth/naturalHeight means it keeps working
 * if a logo is ever swapped.
 */
const markShape = (img) => {
  if (!img) return;

  const apply = () => {
    if (!img.naturalWidth || !img.naturalHeight) return;
    if (img.naturalWidth / img.naturalHeight < 1.35) {
      img.classList.add('is-square-mark');
    }
  };

  // A cached image is already complete before React can attach onLoad, so that
  // event never fires and the class is never applied. Check first, listen second.
  if (img.complete) apply();
  else img.addEventListener('load', apply, { once: true });
};

const ClientLogoStrip = () => {
  const clients = getLogoStripClients();

  const renderTrack = (ariaHidden) => (
    <ul className="client-strip-track" aria-hidden={ariaHidden || undefined}>
      {clients.map((project) => (
        <li key={`${ariaHidden ? 'dup' : 'main'}-${project.id}`} className="client-strip-item">
          <img
            src={project.logo}
            alt={ariaHidden ? '' : project.client_name}
            className="client-strip-logo"
            ref={markShape}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <div className="client-strip">
      <p className="client-strip-label">
        Work spanning seed-stage startups to the Fortune 500
      </p>
      <div className="client-strip-viewport">
        <div className="client-strip-rail">
          {renderTrack(false)}
          {renderTrack(true)}
        </div>
      </div>
    </div>
  );
};

export default ClientLogoStrip;
